import React, { useState } from 'react';
import { Check, XCircle, AlertCircle, SkipForward, Book } from 'react-feather';

export interface SatisfactionCheckProps {
  responseId: string;
  conversationId: string;
  messageId: string;
  originalResponse: string;
  onSatisfactionResponse: (satisfaction: 'yes' | 'partial' | 'no' | 'skip') => void;
  isAuthenticated?: boolean;
  canUseTeaching?: boolean;
  onTeachingSelected?: () => void;
}

/**
 * Satisfaction Check Component
 * Like asking "Did we find the treasure ye were seeking?"
 */
const SatisfactionCheck: React.FC<SatisfactionCheckProps> = ({
  responseId: _responseId,
  conversationId: _conversationId,
  messageId: _messageId,
  originalResponse: _originalResponse,
  onSatisfactionResponse,
  isAuthenticated = false,
  canUseTeaching = false,
  onTeachingSelected
}) => {
  // Props kept for API contract - may be used for analytics in future
  void _responseId;
  void _conversationId;
  void _messageId;
  void _originalResponse;
  const [selectedOption, setSelectedOption] = useState<'yes' | 'partial' | 'no' | 'skip' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOptionSelect = async (option: 'yes' | 'partial' | 'no' | 'skip') => {
    setIsSubmitting(true);
    setSelectedOption(option);
    
    // Notify parent component
    onSatisfactionResponse(option);
    
    setIsSubmitting(false);
  };

  // If already selected, show completion message
  if (selectedOption) {
    return (
      <div className="satisfaction-check-complete">
        <span className="text-xs text-eddy-green flex items-center gap-1">
          <Check size={12} />
          Thank you for your feedback!
        </span>
      </div>
    );
  }

  return (
    <div className="satisfaction-check-container mt-4 pt-4 border-t border-gray-200">
      <div className="satisfaction-check">
        <div className="satisfaction-question flex items-center gap-2 mb-3">
          <AlertCircle size={16} className="text-amber-500" />
          <p className="text-sm text-gray-600">
            Did this answer your question?
          </p>
        </div>
        <div className="satisfaction-options flex gap-3 flex-wrap">
          <button
            onClick={() => handleOptionSelect('yes')}
            disabled={isSubmitting}
            className="feedback-btn flex items-center justify-center gap-2 min-w-[100px] px-4 py-3 rounded-lg border-2 border-gray-300 bg-white hover:border-eddy-green hover:bg-eddy-green/10 hover:text-eddy-green focus:outline-none focus:ring-3 focus:ring-eddy-green/20 active:bg-eddy-green/20 active:scale-95 transition-all duration-150 text-sm font-semibold text-gray-700 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="Yes, fully answered"
          >
            <Check size={18} className="flex-shrink-0" />
            <span>Yes</span>
          </button>
          
          <button
            onClick={() => handleOptionSelect('partial')}
            disabled={isSubmitting}
            className="feedback-btn flex items-center justify-center gap-2 min-w-[100px] px-4 py-3 rounded-lg border-2 border-gray-300 bg-white hover:border-amber-500 hover:bg-amber-50 hover:text-amber-700 focus:outline-none focus:ring-3 focus:ring-amber-200 active:bg-amber-100 active:scale-95 transition-all duration-150 text-sm font-semibold text-gray-700 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="Partially answered"
          >
            <AlertCircle size={18} className="flex-shrink-0" />
            <span>Partially</span>
          </button>
          
          <button
            onClick={() => handleOptionSelect('no')}
            disabled={isSubmitting}
            className="feedback-btn flex items-center justify-center gap-2 min-w-[100px] px-4 py-3 rounded-lg border-2 border-gray-300 bg-white hover:border-red-500 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-3 focus:ring-red-200 active:bg-red-100 active:scale-95 transition-all duration-150 text-sm font-semibold text-gray-700 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="No, not answered"
          >
            <XCircle size={18} className="flex-shrink-0" />
            <span>No</span>
          </button>
          
          <button
            onClick={() => handleOptionSelect('skip')}
            disabled={isSubmitting}
            className="feedback-btn flex items-center justify-center gap-2 min-w-[100px] px-4 py-3 rounded-lg border-2 border-gray-300 bg-white hover:border-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-3 focus:ring-gray-200 active:bg-gray-100 active:scale-95 transition-all duration-150 text-sm font-semibold text-gray-500 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            title="Skip"
          >
            <SkipForward size={18} className="flex-shrink-0" />
            <span>Skip</span>
          </button>
          
          {/* Teaching button - only show for authenticated users with teaching capabilities */}
          {isAuthenticated && canUseTeaching && onTeachingSelected && (
            <button
              onClick={onTeachingSelected}
              disabled={isSubmitting}
              className="feedback-btn flex items-center justify-center gap-2 min-w-[100px] px-4 py-3 rounded-lg border-2 border-gray-300 bg-white hover:border-eddy-green hover:bg-eddy-green/10 hover:text-eddy-green focus:outline-none focus:ring-3 focus:ring-eddy-green/20 active:bg-eddy-green/20 active:scale-95 transition-all duration-150 text-sm font-semibold text-gray-700 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              title="Teach Eddy new information"
            >
              <Book size={18} className="flex-shrink-0" />
              <span>Teach</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SatisfactionCheck;